/**
 * The dirty-aware editor's decisions (D35), pure: what an editor of one
 * versioned resource (a studio, a show style — and the admin screens next)
 * does when someone else changes it while it is open.
 *
 *   no unsaved edits   the newer version replaces the one on screen, in place
 *                      (`reset`), with a brief highlight (`refreshes` counts)
 *   unsaved edits      NOTHING is replaced: a notice says who changed it and
 *                      when, and holds their version, until the user picks
 *                        · keep mine ("Overwrite with mine") — the edit is
 *                          re-based on their version, and the view saves it
 *                          naming their version (optimistic concurrency holds);
 *                        · discard mine ("Discard mine and load theirs")
 *   deleted            a notice, whatever the edits; the edits stay on screen
 *   a save's 409       the same notice as a change heard while dirty — the
 *                      server's current version is theirs
 *
 * There is no merge (D35: a three-way merge dialog is phase 8's). `base` is
 * the version a save names; `reset` tells the view to replace its draft.
 */
import type { HeardChange } from './changeLog.js';

/** Someone else's change the user has not taken in yet. */
export type LiveNotice<T> =
  | { readonly kind: 'updated'; readonly theirs: T; readonly by: string; readonly atMs: number }
  | { readonly kind: 'deleted'; readonly by: string; readonly atMs: number };

export type LiveState<T> = {
  /** The version the draft is made from — what a save names. */
  readonly base: T;
  readonly notice: LiveNotice<T> | null;
  /** How many times `base` was replaced in place (the view flashes on each). */
  readonly refreshes: number;
};

export type LiveEvent<T> =
  /** The resource as the server now has it (a refetch after a change, or a background refetch). */
  | { readonly type: 'fetched'; readonly data: T; readonly dirty: boolean; readonly by: string; readonly atMs: number }
  | { readonly type: 'deleted'; readonly by: string; readonly atMs: number }
  /** A save refused (409): `current` is what the server has. */
  | { readonly type: 'conflict'; readonly current: T; readonly by: string; readonly atMs: number }
  | { readonly type: 'saved'; readonly saved: T }
  | { readonly type: 'discard-mine' }
  | { readonly type: 'keep-mine' };

/** The next state, and the version to replace the view's draft with (null: leave the draft alone). */
export type LiveStep<T> = { readonly state: LiveState<T>; readonly reset: T | null };

export function initialLive<T>(base: T): LiveState<T> {
  return { base, notice: null, refreshes: 0 };
}

function unchanged<T>(state: LiveState<T>): LiveStep<T> {
  return { state, reset: null };
}

function refreshed<T>(state: LiveState<T>, base: T): LiveStep<T> {
  return { state: { base, notice: null, refreshes: state.refreshes + 1 }, reset: base };
}

export function stepLive<T>(state: LiveState<T>, event: LiveEvent<T>, versionOf: (value: T) => number): LiveStep<T> {
  switch (event.type) {
    case 'fetched': {
      // The same version (our own save echoed back, a background refetch), or an older one arriving late: nothing to do.
      if (versionOf(event.data) <= versionOf(state.base)) return unchanged(state);
      if (!event.dirty) return refreshed(state, event.data);
      // Keep the newest of theirs if two changes land before the user decides.
      if (state.notice?.kind === 'updated' && versionOf(state.notice.theirs) >= versionOf(event.data)) return unchanged(state);
      return unchanged({ ...state, notice: { kind: 'updated', theirs: event.data, by: event.by, atMs: event.atMs } });
    }
    case 'deleted':
      return unchanged({ ...state, notice: { kind: 'deleted', by: event.by, atMs: event.atMs } });
    case 'conflict':
      return unchanged({ ...state, notice: { kind: 'updated', theirs: event.current, by: event.by, atMs: event.atMs } });
    case 'saved':
      return { state: { ...state, base: event.saved, notice: null }, reset: event.saved };
    case 'discard-mine':
      return state.notice?.kind === 'updated' ? refreshed(state, state.notice.theirs) : unchanged(state);
    case 'keep-mine':
      // Re-based: the draft stays, and now names their version — the view saves it next.
      return state.notice?.kind === 'updated' ? unchanged({ ...state, base: state.notice.theirs, notice: null }) : unchanged(state);
  }
}

/** How to read a version: its number, and who wrote it and when when the change channel has not said. */
export type VersionReader<T> = {
  readonly versionOf: (value: T) => number;
  readonly authorOf: (value: T) => string;
  readonly writtenAtOf: (value: T) => number;
};

/** Who a change is shown as by: the user the channel named, or "Someone" for a machine writer (replication, a migration). */
export function writerName(heard: HeardChange): string {
  return heard.change.changedBy?.displayName ?? 'Someone';
}

/** Who wrote `value`, and when: the change channel's word when it named this very version, else the record's own. */
export function provenance<T>(heard: HeardChange | null, value: T, reader: VersionReader<T>): { readonly by: string; readonly atMs: number } {
  if (heard !== null && heard.change.version === reader.versionOf(value)) return { by: writerName(heard), atMs: heard.atMs };
  return { by: reader.authorOf(value), atMs: reader.writtenAtOf(value) };
}

/** What the page shows now against what the editor last absorbed: the change heard and the query's data. */
export type LiveInputs<T> = {
  readonly heard: HeardChange | null;
  readonly seenHeard: HeardChange | null;
  readonly latest: T | undefined;
  readonly seenLatest: T | undefined;
  /** Our own save is in flight: what the query shows meanwhile is that save (or its 409, reported apart). */
  readonly saving: boolean;
  readonly isDirty: (base: T) => boolean;
};

/**
 * Fold new inputs into the state: a delete heard is a `deleted` notice; new
 * data is a `fetched` event, judged dirty against the base as it stands after
 * the delete — except while our own save is in flight.
 */
export function absorb<T>(state: LiveState<T>, inputs: LiveInputs<T>, reader: VersionReader<T>): LiveStep<T> {
  let next = state;
  let reset: T | null = null;
  const apply = (event: LiveEvent<T>): void => {
    const stepped = stepLive(next, event, reader.versionOf);
    next = stepped.state;
    if (stepped.reset !== null) reset = stepped.reset;
  };
  const { heard, latest } = inputs;
  if (heard !== inputs.seenHeard && heard !== null && heard.change.op === 'delete') apply({ type: 'deleted', by: writerName(heard), atMs: heard.atMs });
  if (latest !== undefined && latest !== inputs.seenLatest && !inputs.saving) apply({ type: 'fetched', data: latest, dirty: inputs.isDirty(next.base), ...provenance(heard, latest, reader) });
  return { state: next, reset };
}

/** The React state a live editor keeps, as setters (the hook passes its `useState` setters). */
export type LivePorts<T> = {
  readonly state: LiveState<T>;
  readonly setState: (next: LiveState<T> | ((prev: LiveState<T>) => LiveState<T>)) => void;
  readonly setSaving: (saving: boolean) => void;
  readonly onReset: (value: T) => void;
  readonly heard: HeardChange | null;
  readonly reader: VersionReader<T>;
};

/** What the user and the page's save do to a live editor (`useLiveResource`'s actions). */
export type LiveActions<T> = {
  readonly beginSave: () => void;
  readonly saved: (saved: T) => void;
  readonly conflict: (current: T) => void;
  readonly saveFailed: () => void;
  readonly keepMine: () => T | null;
  readonly discardMine: () => void;
};

export function liveActions<T>(ports: LivePorts<T>): LiveActions<T> {
  const { state, setState, setSaving, onReset, reader } = ports;
  const dispatch = (event: LiveEvent<T>): void => {
    const stepped = stepLive(state, event, reader.versionOf);
    setState(stepped.state);
    if (stepped.reset !== null) onReset(stepped.reset);
  };
  return {
    beginSave: () => setSaving(true),
    saved: (saved) => {
      setSaving(false);
      // Functional: a change heard while the save was in flight must not be lost to a stale closure.
      setState((prev) => stepLive(prev, { type: 'saved', saved }, reader.versionOf).state);
      onReset(saved);
    },
    conflict: (current) => {
      setSaving(false);
      const { by, atMs } = provenance(ports.heard, current, reader);
      setState((prev) => stepLive(prev, { type: 'conflict', current, by, atMs }, reader.versionOf).state);
    },
    saveFailed: () => setSaving(false),
    keepMine: () => {
      const theirs = state.notice?.kind === 'updated' ? state.notice.theirs : null;
      dispatch({ type: 'keep-mine' });
      return theirs;
    },
    discardMine: () => dispatch({ type: 'discard-mine' }),
  };
}
